/* PDP delivery calendar. Keep its calculation and variant refresh independent of cart properties. */
if (!customElements.get('delivery-date')) {
  class DeliveryDate extends HTMLElement {
    connectedCallback() {
      this.refresh();
    }

    refresh() {
      const target = this.querySelector('[data-delivery-date]');
      if (!target) return;

      const estimate = this.estimate();
      target.textContent = estimate ? estimate.label : '';
    }

    estimate(now = new Date()) {
      const range = this.parseDays(this.dataset.days);
      if (!range || range.max === 0) return null;
      const excludeWeekends = this.dataset.excludeWeekends !== 'false';
      const excluded = this.parseExcludedDates(this.dataset.excludedDates);
      // Use Warsaw's calendar date and UTC arithmetic, independent of the buyer's timezone/DST.
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(now);
      const part = (type) => parts.find((entry) => entry.type === type).value;
      const today = new Date(`${part('year')}-${part('month')}-${part('day')}T00:00:00Z`);
      const minDate = this.addBusinessDays(today, range.min, excludeWeekends, excluded);
      const maxDate = range.max === range.min
        ? minDate : this.addBusinessDays(today, range.max, excludeWeekends, excluded);
      if (!minDate || !maxDate) return null;
      return {
        label: this.formatRange(minDate, maxDate, 'pl'),
      };
    }

    // Accepts a single day count ("3") or a range ("1-4" = min-max delivery
    // days). Returns { min, max } with min <= max, or null when unusable.
    parseDays(raw) {
      if (raw == null) return null;
      const value = String(raw).trim();

      const rangeMatch = value.match(/^(\d+)\s*[-–]\s*(\d+)$/);
      if (rangeMatch) {
        let min = parseInt(rangeMatch[1], 10);
        let max = parseInt(rangeMatch[2], 10);
        if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) return null;
        if (min > max) [min, max] = [max, min];
        return { min, max };
      }

      if (/^\d+$/.test(value)) {
        const days = parseInt(value, 10);
        if (!Number.isSafeInteger(days) || days < 0) return null;
        return { min: days, max: days };
      }

      return null;
    }

    // Selima: oba końce zakresu w identycznym formacie, każdy z nazwą dnia —
    // "27 sierpnia (wtorek) – 31 sierpnia (niedziela)". Motyw skracał pierwszą
    // datę do samego dnia ("27–31 sierpnia"), przez co końce wyglądały inaczej.
    formatRange(minDate, maxDate, locale) {
      const withWeekday = (date) => {
        const day = this.formatDate(date, locale, { day: 'numeric', month: 'long' });
        const weekday = this.formatDate(date, locale, { weekday: 'long' });
        return `${day} (${weekday})`;
      };

      if (minDate.getTime() === maxDate.getTime()) return withWeekday(maxDate);
      return `${withWeekday(minDate)} – ${withWeekday(maxDate)}`;
    }

    formatDate(date, locale, options) {
      try {
        return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date);
      } catch (e) {
        return new Intl.DateTimeFormat('pl', { ...options, timeZone: 'UTC' }).format(date);
      }
    }

    // Accepts newline/comma/space separated dates: "YYYY-MM-DD" (exact day)
    // or "MM-DD" (recurring every year). Returns a Set of both forms.
    parseExcludedDates(raw) {
      const set = new Set();
      if (!raw) return set;
      raw
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((value) => {
          if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{2}-\d{2}$/.test(value)) {
            set.add(value);
          }
        });
      return set;
    }

    isExcludedDay(date, excludeWeekends, excluded) {
      const weekday = date.getUTCDay();
      if (excludeWeekends && (weekday === 0 || weekday === 6)) return true;

      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return excluded.has(`${year}-${month}-${day}`) || excluded.has(`${month}-${day}`);
    }

    // Adds `days` delivery days to `start`, skipping weekends (optional) and
    // excluded dates. Capped to avoid an infinite loop on pathological config.
    addBusinessDays(start, days, excludeWeekends, excluded) {
      const date = new Date(start);
      let added = 0;
      let guard = 0;
      while (added < days && guard < 3650) {
        date.setUTCDate(date.getUTCDate() + 1);
        guard += 1;
        if (!this.isExcludedDay(date, excludeWeekends, excluded)) added += 1;
      }
      return added === days ? date : null;
    }
  }

  customElements.define('delivery-date', DeliveryDate);
}
